import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, orderBy, query, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function UsersManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (user) => {
    setViewingUser(user);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      await deleteDoc(doc(db, 'users', id));
      alert('User deleted successfully!');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await updateDoc(doc(db, 'users', user.id), {
        isActive: !user.isActive,
        updatedAt: new Date().toISOString(),
      });
      alert(`User ${user.isActive ? 'deactivated' : 'activated'} successfully!`);
      loadUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.mobileNumber?.includes(searchTerm) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === 'all' ? true :
      filterStatus === 'active' ? user.isActive !== false :
      filterStatus === 'inactive' ? user.isActive === false : true;

    return matchesSearch && matchesFilter;
  });

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">User Management</h2>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #FF1493, #E91E63)',
                color: 'white',
                borderRadius: '20px',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Total Users: {users.length}
              </span>
            </div>
          </div>

          {/* Search and Filter */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '24px',
            flexWrap: 'wrap'
          }}>
            <input
              type="text"
              placeholder="Search by username, email, or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: '250px',
                padding: '12px 16px',
                border: '2px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '14px'
              }}
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {loading && <p>Loading users...</p>}

          {!loading && filteredUsers.length === 0 && (
            <p>No users found. {searchTerm && 'Try adjusting your search.'}</p>
          )}

          {!loading && filteredUsers.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Mobile Number</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Joined Date</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.username}</strong>
                      </td>
                      <td>{user.mobileNumber || 'N/A'}</td>
                      <td>{user.email || 'N/A'}</td>
                      <td>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: user.isActive === false
                            ? 'linear-gradient(135deg, #dc3545, #c82333)'
                            : 'linear-gradient(135deg, #28a745, #218838)',
                          color: 'white'
                        }}>
                          {user.isActive === false ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>{formatDate(user.lastLogin) || 'Never'}</td>
                      <td className="actions">
                        <button
                          onClick={() => handleView(user)}
                          className="btn btn-secondary"
                          style={{ marginRight: '8px' }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={user.isActive === false ? 'btn btn-success' : 'btn btn-secondary'}
                          style={{ marginRight: '8px' }}
                        >
                          {user.isActive === false ? 'Activate' : 'Deactivate'}
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="btn btn-danger"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Statistics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginTop: '24px'
        }}>
          <div className="card">
            <h3 style={{
              fontSize: '16px',
              color: '#666',
              marginBottom: '8px'
            }}>
              Total Users
            </h3>
            <p style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #FF1493, #E91E63)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {users.length}
            </p>
          </div>

          <div className="card">
            <h3 style={{
              fontSize: '16px',
              color: '#666',
              marginBottom: '8px'
            }}>
              Active Users
            </h3>
            <p style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #28a745, #218838)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {users.filter(u => u.isActive !== false).length}
            </p>
          </div>

          <div className="card">
            <h3 style={{
              fontSize: '16px',
              color: '#666',
              marginBottom: '8px'
            }}>
              Inactive Users
            </h3>
            <p style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #dc3545, #c82333)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {users.filter(u => u.isActive === false).length}
            </p>
          </div>

          <div className="card">
            <h3 style={{
              fontSize: '16px',
              color: '#666',
              marginBottom: '8px'
            }}>
              New This Week
            </h3>
            <p style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #FF1493, #E91E63)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {users.filter(u => {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return new Date(u.createdAt) > weekAgo;
              }).length}
            </p>
          </div>
        </div>
      </div>

      {/* View User Modal */}
      {showModal && viewingUser && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">User Details</h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Username
                </label>
                <p style={{ fontSize: '16px', color: '#333' }}>
                  {viewingUser.username}
                </p>
              </div>

              <div>
                <label style={{ fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Mobile Number
                </label>
                <p style={{ fontSize: '16px', color: '#333' }}>
                  {viewingUser.mobileNumber || 'Not provided'}
                </p>
              </div>

              <div>
                <label style={{ fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Email
                </label>
                <p style={{ fontSize: '16px', color: '#333' }}>
                  {viewingUser.email || 'Not provided'}
                </p>
              </div>

              <div>
                <label style={{ fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Status
                </label>
                <p style={{ fontSize: '16px', color: '#333' }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    background: viewingUser.isActive === false
                      ? 'linear-gradient(135deg, #dc3545, #c82333)'
                      : 'linear-gradient(135deg, #28a745, #218838)',
                    color: 'white'
                  }}>
                    {viewingUser.isActive === false ? 'Inactive' : 'Active'}
                  </span>
                </p>
              </div>

              <div>
                <label style={{ fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Joined Date
                </label>
                <p style={{ fontSize: '16px', color: '#333' }}>
                  {formatDate(viewingUser.createdAt)}
                </p>
              </div>

              <div>
                <label style={{ fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Last Login
                </label>
                <p style={{ fontSize: '16px', color: '#333' }}>
                  {formatDate(viewingUser.lastLogin) || 'Never logged in'}
                </p>
              </div>

              <div>
                <label style={{ fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Last Updated
                </label>
                <p style={{ fontSize: '16px', color: '#333' }}>
                  {formatDate(viewingUser.updatedAt) || 'N/A'}
                </p>
              </div>

              <div>
                <label style={{ fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>
                  User ID
                </label>
                <p style={{ fontSize: '12px', color: '#666', fontFamily: 'monospace' }}>
                  {viewingUser.id}
                </p>
              </div>
            </div>

            <div className="actions" style={{ marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersManager;
